import React, { useCallback, useEffect, useState } from 'react'
import DocumentHeader from '../../../../documents/components/DocumentHeader'
import CommonInvoiceTemplate from '../../../../documents/components/CommonInvoiceTemplate'
import { SingleQuotationData, TableColumn } from '../../../../types'
import { useNavigate, useParams } from 'react-router-dom'
import { Box, Button } from '@mui/material'
import CommonCreateButton from '../../../../documents/components/CommonCreateButton'
import { request } from '../../../../../../utils/request'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'
import PrintButtons from '../../../../../../components/PrintButtons'
import usePrint from '../../../../../../utils/usePrint'
import PrintableContainer from '../../../../../../components/PrintableContainer'
import { BusinessInfo, TaxInfo } from '../../../../../controlPanel/types'

const ShowQuotation = () => {
  const [loading, setLoading] = useState(true);
  const [singleQuotation, setSingleQuotation] = useState<SingleQuotationData | null>(null);
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo>();
  const [taxInfo, setTaxInfo] = useState<TaxInfo>();
  const params = useParams()
  const navigate = useNavigate();
  const quotationId = params.quotationId

  const { isPrinting, handlePrint } = usePrint();

  const fetchBusinessInfo = useCallback(async () => {
    const res = await request({
      url: 'setting/businessInfo',
      method: 'get'
    })
    if (res.success) {
      setBusinessInfo(res.data)
    }
  }, [])
  useEffect(() => {
    fetchBusinessInfo();
  }, [fetchBusinessInfo])

  const fetchTaxInfo = useCallback(async () => {
    const res = await request({
      url: 'setting/taxInfo',
      method: 'get'
    })
    if (res.success) {
      setTaxInfo(res.data)
    }
  }, [])
  useEffect(() => {
    fetchTaxInfo();
  }, [fetchTaxInfo])

  const fetchSingleQuotation = useCallback(async () => {
    if (!quotationId) return;

    setLoading(true)
    try {
      const res = await request({
        url: `quotation/get-single-quotation/${quotationId}`,
        method: "get",
      })
      if (res.success && res.data) {
        setSingleQuotation(res.data)
      } else {
        toast.error(res.message || "Failed to fetch invoice");
      }
    } catch (error: any) {
      toast.error(error.message || "Error fetching invoice");
    } finally {
      setLoading(false)
    }
  }, [quotationId])

  useEffect(() => {
    fetchSingleQuotation();
  }, [fetchSingleQuotation])

  // Format date to readable format
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return dayjs(dateString).format('DD MMM YYYY');
  };

  const onPrint = () => {
    // force re-render before invoking print
    requestAnimationFrame(() => {
      setTimeout(() => {
        handlePrint('quotation_print', {
          title: `Sales Invoice - ${singleQuotation?.quotationNumber}'}`,
          fileName: `Sales _${singleQuotation?.quotationNumber}`,
          landscape: false,
          showHeader: true,
          showFooter: false,
          customStyles: `
          table { width: 100% !important; border-collapse: collapse !important; }
          th, td { border: 1px solid #ddd !important; padding: 6px !important; }
          th { background-color: #f5f5f5 !important; font-weight: bold !important; }
          .no-print { display: none !important; }
          .invoice-heading {text-align:center; font-size: 22px}
          .flex-center {display:flex; justify-content:space-between; align-items: flex-start; gap:5px}
          .sub-heading {font-size:14px; font-weight:bold}
          .text-bold {font-weight:bold}
          .w-50{width:50%}
          .out-border{border:1px solid black}
        `
        });
      }, 150); // small delay ensures browser repaints
    });
  };

  // Calculate totals from invoice items
  const calculateTotals = () => {
    if (!singleQuotation?.items?.length) {
      return {
        quantity: 0,
        taxableAmount: 0,
        tax: 0,
        amount: 0
      };
    }

    const totals = singleQuotation.items.reduce((acc, item) => {
      acc.quantity += item.quantity || 0;
      acc.amount += item.amount || 0;
      // Calculate tax amount based on item amount and tax rate
      const taxAmount = ((item.amount || 0) * (item.tax?.rate || 0)) / 100;
      acc.tax += taxAmount;
      // Taxable amount is amount minus tax (for GST calculations)
      acc.taxableAmount += (item.amount || 0) - taxAmount;
      return acc;
    }, { quantity: 0, taxableAmount: 0, tax: 0, amount: 0 });

    return totals;
  };

  // Prepare table data from invoice items
  const prepareTableData = () => {
    if (!singleQuotation?.items?.length) return [];

    return singleQuotation.items.map((item, index) => {
      const taxAmount = ((item.amount || 0) * (item.tax?.rate || 0)) / 100;
      return {
        id: item.id || index.toString(),
        item: item.itemName || `Item ${index + 1}`,
        hsnNo: item.hsnNo || 'N/A',
        quantity: item.quantity || 0,
        rate: item.price || 0,
        tax: taxAmount,
        taxPercentage: item.tax?.rate || 0,
        amount: item.amount || 0,
        discount: item.discount?.amount || 0,
        discountPercentage: item.discount?.isPercentage ? item.discount.amount : 0
      };
    });
  };

  // Prepare table columns based on GST type
  const prepareTableColumns = (): TableColumn[] => {
    const baseColumns: TableColumn[] = [
      // { id: 'srNo', label: 'Sr No', align: "left", width: 60 },
      { id: 'item', label: 'Item Description', align: "left", width: 200 },
      { id: 'quantity', label: 'Qty', align: "right", width: 80 },
      { id: 'rate', label: 'Rate (₹)', align: "right", width: 100 },
      { id: 'discount', label: 'Discount (₹)', align: "right", width: 100 },
    ];

    if (singleQuotation?.gstType === 'GST') {
      // baseColumns.push({ id: 'hsnNo', label: 'HSN/SAC', align: "left", width: 100 });
    }

    baseColumns.push(
      // { id: 'taxPercentage', label: 'Tax %', align: "right", width: 80 },
      { id: 'tax', label: 'Tax Amt (₹)', align: "right", width: 100 },
      { id: 'amount', label: 'Amount (₹)', align: "right", width: 120 }
    );

    return baseColumns;
  };

  // Prepare summary items
  const prepareSummaryItems = () => {
    if (!singleQuotation) return [];

    const totals = calculateTotals();
    const summaryItems = [];

    // Taxable Amount
    summaryItems.push({
      label: 'Taxable Amount',
      value: singleQuotation?.taxableAmount,
      variant: 'body2' as const
    });

    // GST Breakdown if GST invoice
    if (singleQuotation.gstType === 'GST' && singleQuotation.taxBreakdown?.length > 0) {
      singleQuotation.taxBreakdown.forEach((taxItem, index) => {
        if (taxItem.cgst > 0) {
          summaryItems.push({
            label: `CGST (${taxItem.cgst}%)`,
            value: (totals.taxableAmount * taxItem.cgst) / 100,
            variant: 'body2' as const
          });
        }
        if (taxItem.sgst > 0) {
          summaryItems.push({
            label: `SGST (${taxItem.sgst}%)`,
            value: (totals.taxableAmount * taxItem.sgst) / 100,
            variant: 'body2' as const
          });
        }
        if (taxItem.igst > 0) {
          summaryItems.push({
            label: `IGST (${taxItem.igst}%)`,
            value: (totals.taxableAmount * taxItem.igst) / 100,
            variant: 'body2' as const
          });
        }
      });
    } else if (singleQuotation.gstType === 'GST') {
      // Fallback if no tax breakdown
      summaryItems.push({
        label: `GST (${singleQuotation.items[0]?.tax?.rate || 0}%)`,
        value: totals.tax,
        variant: 'body2' as const
      });
    }

    // Additional Charges
    if (singleQuotation.charges?.length > 0) {
      singleQuotation.charges.forEach(charge => {
        summaryItems.push({
          label: charge.label,
          value: charge.amount,
          variant: 'body2' as const
        });
      });
    }

    // Discount
    if (singleQuotation.discount?.amount && singleQuotation.discount.amount > 0) {
      const discountLabel = singleQuotation.discount.isPercentage
        ? `Discount (${singleQuotation.discount.amount}%)`
        : 'Discount';
      summaryItems.push({
        label: discountLabel,
        value: singleQuotation.discount.amount,
        variant: 'body2' as const
      });
    }

    // Round Off
    if (singleQuotation.roundOff) {
      const roundOffValue = Math.round(totals.amount + totals.tax) - (totals.amount + totals.tax);
      if (Math.abs(roundOffValue) > 0) {
        summaryItems.push({
          label: 'Round Off',
          value: roundOffValue,
          variant: 'body2' as const
        });
      }
    }

    // Grand Total
    const grandTotal = singleQuotation.totalAmount;
    summaryItems.push({
      label: 'Grand Total',
      value: grandTotal,
      bold: true,
      variant: 'body1' as const
    });

    return summaryItems;
  };

  const handleCloseStatus = async () => {
    try {

      const res = await request({
        url: `quotation/update-quotation-close-status/${quotationId}`,
        method: 'put',
        data: { isClosed: true }
      })
      if (res.success) {
        toast.success('Quotation status changed to CLOSED')
        setTimeout(()=>{
          navigate(0);
        },2000)
      }
    } catch (error) {
      console.log(error)
    }
  }

  const handleOpenStatus = async () => {
    try {

      const res = await request({
        url: `quotation/update-quotation-close-status/${quotationId}`,
        method: 'put',
        data: { isClosed: false }
      })
      if (res.success) {
        toast.success('Quotation status changed to OPEN')
        setTimeout(()=>{
          navigate(0);
        },2000)
      }
    } catch (error) {
      console.log(error)
    }
  }

  // Prepare custom fields for payment info
  const prepareCustomFields = () => {
    if (!singleQuotation) return [];

    const fields = [];

    if (singleQuotation.receivedAmount > 0) {
      fields.push({
        label: "Paid Amount",
        value: singleQuotation.receivedAmount
      });
    }

    if (singleQuotation.balanceAmount > 0) {
      fields.push({
        label: "Balance Due",
        value: singleQuotation.balanceAmount
      });
    }

    return fields;
  };

  if (!singleQuotation && !loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <DocumentHeader
          heading='Sales Invoice'
          subtitle='Invoice not found'
          showBackButton={true}
        />
      </Box>
    );
  }

  const totals = calculateTotals();
  const tableData = prepareTableData();
  const tableColumns = prepareTableColumns();
  const summaryItems = prepareSummaryItems();
  const customFields = prepareCustomFields();

  return (
    <>
      <DocumentHeader
        heading='Quotation'
        subtitle={`#${singleQuotation?.quotationNumber || 'Loading...'}`}
        status={singleQuotation?.status || 'OPEN'}
        statusColor={singleQuotation?.status === 'OPEN' ? 'success' :
          singleQuotation?.status === 'EXPIRED' ? 'error' : singleQuotation?.status === 'CLOSED' ?'warning' : 'primary'}
        showBackButton={true}
      />

      <Box display='flex' justifyContent='flex-end' mb={3} gap={2}>
        {singleQuotation?.status === 'OPEN' ? (<Button color='warning' variant='outlined' onClick={handleCloseStatus}>
          Close Quotation
        </Button>) : singleQuotation?.status === 'CLOSED' ? (<Button color='success' variant='outlined' onClick={handleOpenStatus}>
          Open Quotation
        </Button>) : ""}

        <PrintButtons
          onPrint={onPrint}
          isPrinting={isPrinting}
          printLabel='Print Quotation'
          pdfLabel='Export PDF'
          disabled={loading || !singleQuotation}
          showPDF={false} // Remove PDF button since we don't have export functionality yet
        />
        <CommonCreateButton
          label="Convert to Invoice"
          // onClick={() => alert('Convert to Invoice clicked')}
          onClick={() => navigate(`/accounts/sales/salesInvoice/createSalesInvoice?gstType=${singleQuotation?.gstType}`, {
            state: {
              quotationId: singleQuotation?._id,
              source: 'quotation'
            }
          })}
          disabled={singleQuotation?.status === 'CLOSED' || singleQuotation?.status === 'EXPIRED' || singleQuotation?.status === 'CONVERTED'}
        />
      </Box>

      <PrintableContainer id="quotation_print">
        <CommonInvoiceTemplate
          documentType={singleQuotation?.quotationType || "QUOTATION"}
          title={singleQuotation?.gstType === 'GST' ? "QUOTATION" : "QUOTATION"}
          companyName={businessInfo?.businessName}
          companyAddress={
            (businessInfo?.address) ? (`${businessInfo.address?.[0]?.line1} ${businessInfo.address?.[0]?.city} ${businessInfo.address?.[0]?.state}  ${businessInfo.address?.[0]?.pinCode}`) : 'Address not mentioned'
          }
          companyPhone={(businessInfo?.contact?.phone?.[0]?.phoneNo) ? (`${businessInfo?.contact?.phone?.[0]?.phoneNo}`) : '+91 0000000000'}
          companyGSTIN={singleQuotation?.gstType === 'GST' ? taxInfo?.taxDetails?.gstNumber : "" }
          documentNumber={singleQuotation?.quotationNumber || ''}
          documentDate={formatDate(singleQuotation?.quotationDate || singleQuotation?.date || '')}
          dueDate={formatDate(singleQuotation?.dueDate || '')}
          partyHeading='Bill To'
          partyName={singleQuotation?.party?.partyName || 'N/A'}
          partyLocation={singleQuotation?.party?.location}
          partyAddress={
            (singleQuotation?.party?.address) ? (`${singleQuotation.party.address.line1} ${singleQuotation.party.address.city} ${singleQuotation.party.address.state}  ${singleQuotation.party.address.pinCode}`) : 'Address not available'
          }
          partyPhone={singleQuotation?.party?.contact?.phone?.[0]?.phoneNo || 'N/A'}
          partyGSTIN={singleQuotation?.party?.gstNumber || 'N/A'}
          dueDateLabel={"Expiry Date"}
          tableColumns={tableColumns}
          tableData={tableData}
          tableTotals={{
            quantity: totals.quantity,
            taxableAmount: totals.taxableAmount,
            tax: totals.tax,
            amount: totals.amount
          }}
          amounts={{
            totalAmount: singleQuotation?.totalAmount,
            taxableAmount: singleQuotation?.taxableAmount,
            subtotal: singleQuotation?.subtotal,
            receivedAmount: singleQuotation?.receivedAmount,
            balanceAmount: singleQuotation?.balanceAmount
          }}
          summaryItems={summaryItems}
          customFields={customFields}
          showLoading={loading}
          showLoadingText="Fetching Quotation..."
          // notes={singleQuotation?.notes || ''}
          termsConditions={singleQuotation?.terms || ''}
          gstType={singleQuotation?.gstType}
          invoiceStatus={singleQuotation?.status}
        />
      </PrintableContainer>
    </>
  )
}

export default ShowQuotation